<template>
  <v-dialog v-model="showDatasourceConfirmation" max-width="500px" persistent>
    <v-card>
      <v-toolbar flat color="secondary">
        <v-toolbar-title class="text-white">
          {{ $t("labels.share-with-datasources") }}
        </v-toolbar-title>
      </v-toolbar>
      <v-card-text class="mt-2">
        <p>{{ $t("strings.share-datasources-intro") }}</p>
        <v-list density="compact" class="mt-2">
          <v-list-item
            v-for="service in pendingServices"
            :key="service.id"
            :title="service.name"
            :subtitle="service.id"
          >
            <template v-slot:prepend>
              <v-icon class="opacity-100" color="primary">mdi-database</v-icon>
            </template>
          </v-list-item>
        </v-list>
        <p class="mt-4">
          {{ $t("strings.confirm-share-datasources-message") }}
        </p>
      </v-card-text>
      <v-divider></v-divider>
      <v-card-actions class="justify-end pb-4 pr-4">
        <v-btn color="primary" variant="text" @click="cancelShare">
          {{ $t("labels.cancel") }}
        </v-btn>
        <v-btn color="secondary" variant="text" @click="confirmShare">
          {{ $t("labels.confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-row v-if="ownerId === loggedUserId" dense>
    <v-col cols="12">
      <h3 class="text-primary font-weight-medium">
        {{ $t("labels.share-to-collaborate") }}
      </h3>
      <small class="text-grey"
        >{{ $t("labels.add-users-to-collaborate") }}
      </small>
    </v-col>
    <v-col cols="12">
      <v-autocomplete
        variant="outlined"
        density="compact"
        persistent-hint
        :search="user"
        @update:search="user = ''"
        :no-data-text="$t('labels.no-data-available')"
        :placeholder="$t('labels.search-user')"
        item-title="username"
        :item-value="(item) => item"
        :items="users"
        append-icon=""
        prepend-icon=""
        filled
      >
        <template v-slot:item="{ props, item }">
          <v-list-item
            @click="shareUser(item.raw)"
            class="users-list"
            v-bind="props"
          ></v-list-item>
        </template>
      </v-autocomplete>
    </v-col>
  </v-row>
  <v-row dense>
    <v-col cols="12">
      <v-list three-line>
        <template v-if="sharedUsers.length > 0">
          <v-list-item
            v-for="(item, index) in sharedUsers"
            :key="`user` + index"
          >
            <template v-slot:prepend>
              <v-icon
                v-if="ownerId === item._id"
                class="text-h4 text-primary opacity-100"
                >mdi-account-circle</v-icon
              >
              <v-icon v-else class="text-h4 text-primary opacity-100"
                >mdi-account-circle-outline</v-icon
              >
            </template>
            <v-list-item-title
              >{{ item.firstname }} {{ item.lastname }}
            </v-list-item-title>
            <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
            <template v-slot:append>
              <v-list-item-action
                v-if="ownerId === item._id"
                class="flex-row align-center"
              >
                <small class="text-high-emphasis opacity-60 text-overline">
                  {{ $t("labels.owner") }}
                </small>
                <v-spacer></v-spacer>
                <v-icon class="ml-2 text-primary">
                  mdi-crown-circle-outline
                </v-icon>
              </v-list-item-action>
              <v-list-item-action v-else class="flex-row align-center">
                <small class="text-high-emphasis opacity-60 text-overline">
                  {{ $t("labels.editor") }}
                </small>
                <v-spacer></v-spacer>
                <v-tooltip
                  location="top"
                  :disabled="ownerId === loggedUserId"
                >
                  <template v-slot:activator="{ props }">
                    <span v-bind="props">
                      <v-icon
                        v-if="ownerId !== item._id"
                        class="ml-2 text-secondary"
                        @click="unshareUser(item, index)"
                        :disabled="ownerId !== loggedUserId"
                      >
                        mdi-close-circle-outline
                      </v-icon>
                    </span>
                  </template>
                  <span>{{ $t("strings.unshare-user-tooltip") }}</span>
                </v-tooltip>
              </v-list-item-action>
            </template>
          </v-list-item>
        </template>
        <div v-else>{{ $t("labels.no-shared") }}</div>
      </v-list>
    </v-col>
  </v-row>
  <v-row v-if="ownerId === loggedUserId" dense>
    <v-col cols="12">
      <h3 class="text-primary font-weight-medium">
        {{ $t("labels.share-limitation") }}
      </h3>
      <small class="text-grey"> {{ $t("labels.view-only-mode") }}</small>
    </v-col>
    <v-col cols="12">
      <v-select
        variant="outlined"
        density="compact"
        :value="selectItems[0]"
        :no-data-text="$t('labels.no-data-available')"
        :items="selectItems"
        no-details
      >
      </v-select>
    </v-col>
  </v-row>
</template>

<script>
import utils from "@/mixins/utils";
import axios from "axios";
import share from "@/mixins/Shareform";

export default {
  name: "ShareForm",
  mixins: [utils, share],
  props: {
    itemToShare: Object,
    realm: String,
    root: String,
    shareFormConfig: {},
  },
  data() {
    return {
      selectItems: [],
      sharedUsers: [],
      users: [],
      apiUrl: this.shareFormConfig.api,
      tenantId: this.realm,
      usersToShare: [],
      user: null,
      loggedUserId: "",
      showDatasourceConfirmation: false,
      pendingSharePayload: null,
      pendingUser: null,
      pendingServices: [],
    };
  },
  mounted() {
    this.loggedUserId = localStorage.getItem("loggedUserId");
    this.selectItems = [
      this.$t("labels.no-one"),
      this.$t("labels.all-members"),
      this.$t("labels.everyone"),
    ];
    document.addEventListener("language-localstorage-changed", (event) => {
      this.setLocale(event.detail.storage);
    });
    this.getItemToShare();
  },
  computed: {
    ownerId() {
      return this.itemLoaded?.owner || this.itemToShare?.owner || "";
    },
  },
  methods: {
    shareUser(user) {
      if (user) {
        const shareUser = { user: user._id, edit: true };
        this.usersToShare.push(shareUser);
        axios
          .patch(
            this.apiUrl +
              "/" +
              this.tenantId +
              "/" +
              this.root +
              "/" +
              this.itemToShare._id +
              "/share",
            this.usersToShare,
            this.opts,
          )
          .then((response) => {
            if (
              response.status === 202 &&
              response.data?.needsConfirmation === true
            ) {
              this.pendingSharePayload = [...this.usersToShare];
              this.pendingUser = user;
              this.pendingServices = response.data.services || [];
              this.showDatasourceConfirmation = true;
            } else {
              this.sharedUsers.push(user);
              this.users.splice(this.users.indexOf(user), 1);
              document.dispatchEvent(new CustomEvent("refresh-item"));
              this.user = "";
            }
          })
          .catch((err) => {
            console.error(err);
          });
      }
    },
    confirmShare() {
      axios
        .patch(
          this.apiUrl +
            "/" +
            this.tenantId +
            "/" +
            this.root +
            "/" +
            this.itemToShare._id +
            "/share?confirmed=true",
          this.pendingSharePayload,
          this.opts,
        )
        .then(() => {
          this.sharedUsers.push(this.pendingUser);
          this.users.splice(this.users.indexOf(this.pendingUser), 1);
          document.dispatchEvent(new CustomEvent("refresh-item"));
          this.user = "";
          this.resetPendingShare();
        })
        .catch((err) => {
          console.error(err);
          this.resetPendingShare();
        });
    },
    cancelShare() {
      this.usersToShare.pop();
      this.resetPendingShare();
    },
    resetPendingShare() {
      this.showDatasourceConfirmation = false;
      this.pendingSharePayload = null;
      this.pendingUser = null;
      this.pendingServices = [];
    },
    unshareUser(user, index) {
      if (user) {
        const userToUnshare = [user._id];
        axios
          .patch(
            this.apiUrl +
              "/" +
              this.tenantId +
              "/" +
              this.root +
              "/" +
              this.itemToShare._id +
              "/unshare",
            userToUnshare,
            this.opts,
          )
          .then(() => {
            this.sharedUsers.splice(index, 1);
            this.users.push(user);
            document.dispatchEvent(new CustomEvent("refresh-item"));
            this.user = "";
          })
          .catch((err) => {
            console.error(err);
          });
      }
    },
  },
};
</script>

<style scoped></style>
